/* AttachmentsList — upload + list + download + delete for client folder.
 * Used by Další údaje tab. */
import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Image, Download, Trash2, Loader2, Paperclip, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { localeUI } from "@/lib/prekladyUI";

/* V prohlížeči relativně (/api → proxy / ingress), stejně jako lib/api.js. Absolutní
 * REACT_APP_BACKEND_URL tu dřív dělal cross-origin volání: lokálně CORS blok
 * (přílohy u kandidáta v Náboru), na mobilu riziko neposlaných cookies. 24. 10. 2026 */
const API = typeof window !== "undefined" ? "" : (process.env.REACT_APP_BACKEND_URL || "");

// Robustnost: endpoint může vrátit ne-pole nebo null-položky.
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

const TYPE_ICONS = {
  "application/pdf": FileText,
  "image/jpeg": Image,
  "image/png": Image,
  "image/webp": Image,
};

function humanSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentsList({ contactId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    if (!contactId) return;
    setLoading(true);
    setError(false);
    try {
      const r = await fetch(`${API}/api/crm/contacts/${contactId}/attachments`, { credentials: "include" });
      if (r.ok) { setItems(asArr(await r.json())); }
      else {
        // HTTP 4xx/5xx: chyba != prázdno — zobraz error+retry, ne falešné „žádné přílohy".
        setError(true);
        setItems([]);
      }
    } catch {
      // síťová chyba (offline / CORS) — přílohy se prostě nenačtou, kartu nesmí shodit
      setError(true);
      setItems([]);
    } finally { setLoading(false); }
  };
  // Reset při přepnutí klienta → žádné přílohy předchozího klienta a čerstvé načtení.
  useEffect(() => { setItems([]); setError(false); load(); /* eslint-disable-next-line */ }, [contactId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Max 15 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/crm/contacts/${contactId}/attachments`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Upload selhal");
        return;
      }
      toast.success("✓ Příloha nahrána");
      await load();
    } catch {
      toast.error("Nahrání selhalo (síť)");
    } finally { setUploading(false); }
  };

  const onDelete = async (aid) => {
    if (!window.confirm("Smazat přílohu?")) return;
    try {
      const r = await fetch(`${API}/api/crm/contacts/${contactId}/attachments/${aid}`, {
        method: "DELETE", credentials: "include",
      });
      if (r.ok) {
        toast.success("Smazáno");
        setItems((i) => i.filter((x) => x.id !== aid));
      } else {
        toast.error("Smazání selhalo");
      }
    } catch {
      toast.error("Smazání selhalo (síť)");
    }
  };

  return (
    <div data-testid="attachments-list">
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.heic,.txt,.csv"
        onChange={onUpload}
        className="hidden"
        data-testid="attachments-upload-input"
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading || !contactId}
        className="w-full flex items-center justify-center gap-1.5 py-2 mb-2 rounded-md font-semibold text-[12px] transition-all disabled:opacity-50"
        style={{ backgroundColor: "var(--accent)", color: "var(--on-accent)", border: "1px solid #A84A29" }}
        data-testid="attachments-upload-btn"
      >
        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
        {uploading ? "Nahrávám…" : "+ Nahrát soubor"}
      </button>

      {loading && <div className="text-center py-3 text-drobny" style={{ color: "#8C8378" }}>Načítám…</div>}

      {!loading && error && (
        <div className="text-center py-3 text-drobny" data-testid="attachments-error" style={{ color: "#B0472B" }}>
          <AlertTriangle size={20} className="mx-auto mb-1" />
          Přílohy se nepodařilo načíst
          <div>
            <button
              onClick={load}
              data-testid="attachments-retry"
              className="mt-1 underline font-semibold"
              style={{ color: "var(--accent)" }}
            >
              Zkusit znovu
            </button>
          </div>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-center py-3 text-drobny" style={{ color: "#8C8378" }}>
          <Paperclip size={20} className="mx-auto mb-1 opacity-40" />
          Žádné přílohy
        </div>
      )}

      <div className="space-y-1">
        {items.map((a) => {
          const Icon = TYPE_ICONS[a.content_type] || FileText;
          return (
            <div
              key={a.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded text-drobny"
              style={{ background: "var(--surface)", border: "1px solid #E8DCC8" }}
              data-testid={`attachment-${a.id}`}
            >
              <Icon size={14} style={{ color: "var(--accent)" }} className="flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium" style={{ color: "var(--ink)" }}>{a.filename}</div>
                <div className="text-drobny" style={{ color: "#8C8378" }}>
                  {humanSize(a.size_bytes)} · {new Date(a.uploaded_at).toLocaleDateString(localeUI())}
                </div>
              </div>
              <a
                href={`${API}${a.download_url}`}
                target="_blank" rel="noreferrer"
                className="p-1 rounded hover:bg-[#F4ECE0]"
                title="Stáhnout"
              >
                <Download size={12} style={{ color: "#5C5249" }} />
              </a>
              <button
                onClick={() => onDelete(a.id)}
                className="p-1 rounded hover:bg-red-50"
                title="Smazat"
                data-testid={`attachment-delete-${a.id}`}
              >
                <Trash2 size={12} style={{ color: "#DC2626" }} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
